import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Tier configs — each tier gets progressively more spectacular
const TIER_CONFIG = {
  simple: {
    duration: 2800,
    bgOpacity: 0.4,
    particles: 30,
    burstWaves: 1,
    giftScale: 1.1,
    shockwave: false,
    rays: false,
    goldenRain: false,
    screenShake: false,
  },
  medium: {
    duration: 3500,
    bgOpacity: 0.55,
    particles: 50,
    burstWaves: 2,
    giftScale: 1.3,
    shockwave: true,
    rays: false,
    goldenRain: false,
    screenShake: false,
  },
  complex: {
    duration: 4500,
    bgOpacity: 0.7,
    particles: 80,
    burstWaves: 3,
    giftScale: 1.5,
    shockwave: true,
    rays: true,
    goldenRain: true,
    screenShake: false,
  },
  epic: {
    duration: 6000,
    bgOpacity: 0.85,
    particles: 120,
    burstWaves: 4,
    giftScale: 1.8,
    shockwave: true,
    rays: true,
    goldenRain: true,
    screenShake: true,
  },
};

const PARTICLE_PALETTES = {
  small: ["#FFD700", "#FFA500", "#FF6B6B", "#FFB347"],
  premium: ["#FFD700", "#FF69B4", "#4ECDC4", "#A78BFA", "#FFA500"],
  luxury: ["#FFD700", "#FF1493", "#00CED1", "#FF4500", "#9370DB", "#32CD32"],
  ultra: ["#FFD700", "#FFA500", "#FF6347", "#FF69B4", "#8B5CF6", "#00CED1", "#32CD32", "#FF1493"],
};

// Premium sound effects — unique melody per tier
const playGiftSound = (tier) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();

    const playNote = (freq, start, dur, vol, type = "sine") => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(vol, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
      osc.start(start);
      osc.stop(start + dur);
    };

    const t = ctx.currentTime;

    if (tier === "epic") {
      // Triumphant cinematic fanfare — brass + shimmer
      const brass = [261.63, 329.63, 392, 523.25, 659.25, 783.99, 1046.5];
      brass.forEach((f, i) => {
        playNote(f, t + i * 0.12, 0.7, 0.10, "sawtooth");
        playNote(f * 2, t + i * 0.12 + 0.05, 0.4, 0.04, "sine"); // shimmer
      });
      // Final chord
      [523.25, 659.25, 783.99, 1046.5].forEach(f => {
        playNote(f, t + 0.9, 1.2, 0.08, "triangle");
      });
      // Deep impact
      playNote(130.81, t + 0.85, 1.5, 0.12, "triangle");
    } else if (tier === "complex") {
      // Elegant fanfare — ascending arpeggio with harmonics
      const melody = [392, 493.88, 587.33, 698.46, 783.99, 1046.5];
      melody.forEach((f, i) => {
        playNote(f, t + i * 0.1, 0.6, 0.09, "triangle");
        playNote(f * 1.5, t + i * 0.1 + 0.02, 0.3, 0.03, "sine");
      });
      // Sustain chord
      [523.25, 659.25, 783.99].forEach(f => playNote(f, t + 0.7, 0.8, 0.06, "sine"));
    } else if (tier === "medium") {
      // Rich chime — warm ascending with overtones
      const melody = [523.25, 587.33, 659.25, 783.99, 1046.5];
      melody.forEach((f, i) => {
        playNote(f, t + i * 0.1, 0.5, 0.08, "sine");
        if (i >= 2) playNote(f * 2, t + i * 0.1 + 0.01, 0.25, 0.025, "sine");
      });
    } else {
      // Simple: gentle 3-note chime
      [659.25, 783.99, 1046.5].forEach((f, i) => {
        playNote(f, t + i * 0.12, 0.4, 0.06, "sine");
      });
    }
  } catch (e) { /* silent */ }
};

// Individual particle
const Particle = ({ index, total, tier, colors, delay = 0 }) => {
  const angle = (index / total) * Math.PI * 2 + (Math.random() - 0.5) * 0.5;
  const distance = 120 + Math.random() * (tier === "epic" ? 350 : tier === "complex" ? 250 : 180);
  const size = tier === "epic" ? 4 + Math.random() * 6 : tier === "complex" ? 3 + Math.random() * 5 : 2 + Math.random() * 4;
  const color = colors[index % colors.length];
  const duration = 1 + Math.random() * 1.5;

  return (
    <motion.div
      initial={{ x: 0, y: 0, opacity: 1, scale: 1.5 }}
      animate={{
        x: Math.cos(angle) * distance,
        y: Math.sin(angle) * distance,
        opacity: 0,
        scale: 0,
      }}
      transition={{ duration, delay: delay + index * 0.01, ease: "easeOut" }}
      style={{
        position: "absolute",
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        boxShadow: `0 0 ${size * 2}px ${color}`,
        left: "50%",
        top: "50%",
      }}
    />
  );
};

// Shockwave ring
const Shockwave = ({ delay = 0 }) => (
  <motion.div
    initial={{ scale: 0, opacity: 0.8 }}
    animate={{ scale: 4, opacity: 0 }}
    transition={{ duration: 1.2, delay, ease: "easeOut" }}
    style={{
      position: "absolute",
      width: 100,
      height: 100,
      borderRadius: "50%",
      border: "2px solid rgba(255, 215, 0, 0.6)",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
      boxShadow: "0 0 30px rgba(255, 215, 0, 0.3)",
    }}
  />
);

// Light rays
const LightRays = () => (
  <motion.div
    initial={{ opacity: 0, rotate: 0 }}
    animate={{ opacity: [0, 0.6, 0], rotate: 180 }}
    transition={{ duration: 3, ease: "easeInOut" }}
    className="absolute inset-0 flex items-center justify-center"
    style={{ pointerEvents: "none" }}
  >
    {[...Array(12)].map((_, i) => (
      <motion.div
        key={i}
        style={{
          position: "absolute",
          width: 2,
          height: "40vh",
          background: `linear-gradient(to top, transparent, rgba(255, 215, 0, ${0.15 + (i % 3) * 0.08}), transparent)`,
          transformOrigin: "bottom center",
          transform: `rotate(${i * 30}deg)`,
          bottom: "50%",
          left: "50%",
        }}
      />
    ))}
  </motion.div>
);

// Golden rain particle
const GoldenRainDrop = ({ index }) => {
  const x = Math.random() * 100;
  const delay = Math.random() * 2;
  const duration = 1.5 + Math.random() * 2;
  const size = 2 + Math.random() * 3;

  return (
    <motion.div
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: "100vh", opacity: [0, 1, 1, 0] }}
      transition={{ duration, delay, ease: "linear" }}
      style={{
        position: "absolute",
        left: `${x}%`,
        top: 0,
        width: size,
        height: size * 3,
        borderRadius: size,
        background: "linear-gradient(to bottom, #FFD700, #FFA500)",
        boxShadow: "0 0 4px #FFD700",
      }}
    />
  );
};

// Main gift animation
export const CinematicGiftAnimation = ({ gift, isVisible, onComplete }) => {
  const [phase, setPhase] = useState("idle");
  const timerRef = useRef(null);

  const tier = gift?.animation || "simple";
  const config = TIER_CONFIG[tier] || TIER_CONFIG.simple;
  const colors = PARTICLE_PALETTES[gift?.category] || PARTICLE_PALETTES.small;

  const cleanup = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setPhase("idle");
    onComplete?.();
  }, [onComplete]);

  useEffect(() => {
    if (!isVisible || !gift) return;
    playGiftSound(tier);
    setPhase("enter");
    const t1 = setTimeout(() => setPhase("burst"), 400);
    const t2 = setTimeout(() => setPhase("exit"), config.duration - 800);
    timerRef.current = setTimeout(cleanup, config.duration);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(timerRef.current); };
  }, [isVisible, gift, tier, config.duration, cleanup]);

  if (!isVisible || !gift || phase === "idle") return null;

  const giftImg = gift.image || `/images/gifts/${gift.id || gift.gift_type}.png`;

  return (
    <AnimatePresence>
      <motion.div
        key="gift-cinema"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
        style={{ pointerEvents: "none" }}
      >
        {/* Cinematic dark background */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: config.bgOpacity }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black"
        />

        {/* Radial glow behind gift */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: [0, 2, 2.5], opacity: [0, 0.6, 0] }}
          transition={{ duration: 2, ease: "easeOut", delay: 0.2 }}
          className="absolute rounded-full"
          style={{
            width: 200,
            height: 200,
            background: `radial-gradient(circle, ${colors[0]}88, ${colors[1] || colors[0]}44, transparent)`,
          }}
        />

        {/* Light rays — luxury/epic */}
        {config.rays && <LightRays />}

        {/* Shockwave rings */}
        {config.shockwave && (
          <>
            <Shockwave delay={0.3} />
            {config.burstWaves >= 2 && <Shockwave delay={0.6} />}
            {config.burstWaves >= 3 && <Shockwave delay={1.0} />}
          </>
        )}

        {/* Particle burst waves */}
        {[...Array(config.burstWaves)].map((_, wave) => (
          <div key={wave} className="absolute" style={{ left: "50%", top: "50%" }}>
            {[...Array(Math.floor(config.particles / config.burstWaves))].map((_, i) => (
              <Particle
                key={`${wave}-${i}`}
                index={i}
                total={Math.floor(config.particles / config.burstWaves)}
                tier={tier}
                colors={colors}
                delay={wave * 0.4 + 0.3}
              />
            ))}
          </div>
        ))}

        {/* Golden rain — complex/epic */}
        {config.goldenRain && (
          <div className="absolute inset-0">
            {[...Array(tier === "epic" ? 40 : 20)].map((_, i) => (
              <GoldenRainDrop key={i} index={i} />
            ))}
          </div>
        )}

        {/* Screen shake container */}
        <motion.div
          animate={
            config.screenShake && phase === "burst"
              ? { x: [0, -4, 4, -3, 3, -2, 2, 0], y: [0, 3, -3, 2, -2, 1, -1, 0] }
              : {}
          }
          transition={{ duration: 0.5, delay: 0.3 }}
          className="relative flex flex-col items-center"
        >
          {/* Gift image — dramatic entrance */}
          <motion.div
            initial={{ scale: 0, y: 100, opacity: 0, rotate: -15 }}
            animate={
              phase === "exit"
                ? { scale: 2, opacity: 0, y: -50 }
                : { scale: config.giftScale, y: 0, opacity: 1, rotate: 0 }
            }
            transition={
              phase === "exit"
                ? { duration: 0.8, ease: "easeIn" }
                : { type: "spring", stiffness: 200, damping: 15, delay: 0.1 }
            }
            style={{
              filter:
                tier === "epic"
                  ? "drop-shadow(0 0 40px rgba(255,215,0,0.9)) drop-shadow(0 0 80px rgba(255,165,0,0.5))"
                  : tier === "complex"
                  ? "drop-shadow(0 0 30px rgba(255,215,0,0.7)) drop-shadow(0 0 60px rgba(255,165,0,0.4))"
                  : tier === "medium"
                  ? "drop-shadow(0 0 20px rgba(255,215,0,0.6))"
                  : "drop-shadow(0 0 15px rgba(255,215,0,0.4))",
            }}
          >
            <motion.img
              src={giftImg}
              alt={gift.name}
              className="w-32 h-32 md:w-40 md:h-40 object-contain"
              animate={
                tier === "epic"
                  ? { rotate: [0, 5, -5, 3, -3, 0], scale: [1, 1.05, 1, 1.03, 1] }
                  : tier === "complex"
                  ? { rotate: [0, 3, -3, 0], scale: [1, 1.03, 1] }
                  : {}
              }
              transition={{ duration: 2, repeat: 1, ease: "easeInOut" }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </motion.div>

          {/* Gift name text — appears below */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={phase === "exit" ? { opacity: 0, y: -20 } : { opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            className="mt-4 text-center"
          >
            <p
              className="text-2xl font-black tracking-tight"
              style={{
                background:
                  tier === "epic"
                    ? "linear-gradient(135deg, #FFD700, #FFA500, #FFD700)"
                    : tier === "complex"
                    ? "linear-gradient(135deg, #FFD700, #FF69B4)"
                    : "linear-gradient(135deg, #FFFFFF, #E0E0E0)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "none",
                filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.5))",
              }}
            >
              {gift.name || gift.gift_name}
            </p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              transition={{ delay: 0.8 }}
              className="text-sm text-white/70 mt-1 font-medium"
            >
              inviato!
            </motion.p>
          </motion.div>
        </motion.div>

        {/* Floating mini-gifts — epic only */}
        {tier === "epic" &&
          [...Array(8)].map((_, i) => (
            <motion.img
              key={`mini-${i}`}
              src={giftImg}
              alt=""
              className="absolute w-8 h-8 object-contain opacity-60"
              style={{ left: "50%", top: "50%" }}
              initial={{ x: 0, y: 0, scale: 0.4, opacity: 0.7 }}
              animate={{
                x: (Math.random() - 0.5) * 400,
                y: -250 - Math.random() * 200,
                scale: 0,
                opacity: 0,
                rotate: 360 * (Math.random() > 0.5 ? 1 : -1),
              }}
              transition={{ duration: 2.5, delay: 0.8 + i * 0.15, ease: "easeOut" }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ))}
      </motion.div>
    </AnimatePresence>
  );
};

export default CinematicGiftAnimation;
